import type {
	BaseDataItem,
	Component,
	RefArrayViewItem,
	Style,
	VariantDataItem,
	VariantRelationViewItem,
} from '@wix/thunderbolt-becky-types'
import type { CompDesign } from '@wix/thunderbolt-components'
import { fixStyleDataItem } from '@wix/thunderbolt-becky-root'
import { compInfoNodes } from '@wix/thunderbolt-component-isolation'
import { createCssCompNode } from '../cssCompNode'
import { isRefArray, isVariantRelation } from '../../utils/variantsUtils'
import { toBreakpointVariantsMap } from '../../utils/toBreakpointVariantsMap'
import type { NodeRefs } from '@wix/thunderbolt-catharsis'
import { createCompNode, envRefs, themeRefs } from '@wix/thunderbolt-catharsis'
import _ from 'lodash'
import type { FeatureRefs } from '../cssFeatures.types'
import { withModes } from '../modesCompNode'
import type { Design } from '../../shared.types'
import { resolveSpxAttribute } from '@wix/thunderbolt-commons'
import { CSS_SHORTHAND_PROPERTIES } from './styleProps/utils/consts'
import type { BuilderStyle } from '@wix/document-services-types'

const STYLE_OVERRIDEN_PROPS = ['styleId' as const]
const DESIGN_OVERRIDEN_PROPS = ['designQuery' as const]
const MOBILE_VARIANT_ID = 'MOBILE-VARIANT'

export const createEmptyStyleItem = (component: Readonly<Component>): Style => ({
	id: component.styleId || _.uniqueId('styleId-'),
	type: 'ComponentStyle' as const,
	skin: component.skin!,
	style: { properties: {} },
})

const omitValuesAccordingToViewMode = (
	values: Array<BaseDataItem | VariantRelationViewItem<BaseDataItem>>,
	isMobileView: boolean
): Array<BaseDataItem | VariantRelationViewItem<BaseDataItem>> => {
	const predicate = (variants: Array<VariantDataItem>) => {
		const isMobileVariant = variants.find((v) => v.id === MOBILE_VARIANT_ID)
		return isMobileView ? isMobileVariant : !isMobileVariant
	}
	return values.filter(
		(currentDesignItem) => !isVariantRelation(currentDesignItem) || predicate(currentDesignItem.variants)
	)
}

const variants = createCompNode({
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) => ({
		maybeRefArray: component.styleId ? refs.styleId(component.styleId) : undefined,
		themeFonts: themeRefs.themeFonts,
		sitesEditorName: envRefs.editorName,
		experiments: envRefs.experiments,
		publicTopology: envRefs.publicTopology,
		componentsSkinParams: envRefs.componentsSkinParams,
	}),
	toViewItem: (
		component,
		{ maybeRefArray, themeFonts, sitesEditorName, experiments, publicTopology, componentsSkinParams }
	) => {
		const items = isRefArray(maybeRefArray)
			? maybeRefArray.values
			: [maybeRefArray || createEmptyStyleItem(component)]

		const { staticMediaUrl } = publicTopology

		const isSiteFromEditor = sitesEditorName === 'Studio' || !!experiments['specs.thunderbolt.forceStudio']

		return {
			type: 'variants' as const,
			css: toBreakpointVariantsMap(items, (item) =>
				component.builderType
					? experiments['specs.thunderbolt.newStyleApi']
						? handleBuilderStyleApi(item)
						: item
					: fixStyleDataItem(
						item,
						component.componentType,
						staticMediaUrl,
						themeFonts,
						isSiteFromEditor,
						componentsSkinParams
					)
			),
		}
	},
})

const styleModes = withModes(
	createCompNode({
		getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) => ({
			styleId: component.styleId ? refs.styleId(component.styleId) : undefined,
			themeFonts: themeRefs.themeFonts,
			publicTopology: envRefs.publicTopology,
			componentsSkinParams: envRefs.componentsSkinParams,
		}),
		toViewItem: (component, { styleId, themeFonts, publicTopology, componentsSkinParams }) =>
			fixStyleDataItem(
				(styleId as Style | undefined) || createEmptyStyleItem(component),
				component.componentType,
				publicTopology.staticMediaUrl,
				themeFonts,
				false,
				componentsSkinParams
			),
	}),
	STYLE_OVERRIDEN_PROPS
)

const designModes = withModes(
	createCompNode({
		getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) =>
			component.designQuery ? refs.designQuery(component.designQuery) : null,
		toViewItem: (component, designData) => designData,
	}),
	DESIGN_OVERRIDEN_PROPS
)

export const compStyles = createCssCompNode('styleBased', 'compStyles', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) => {
		const modesDependencies = styleModes.getDependencies(component, refs)

		return Object.keys(modesDependencies.modes).length > 1
			? modesDependencies
			: variants.getDependencies(component, refs)
	},
	toViewItem: (component, dependencies) => {
		if ('modes' in dependencies) {
			return styleModes.toViewItem(component, dependencies)
		}
		return variants.toViewItem(component, dependencies)
	},
})

export const compDesigns = createCssCompNode('styleBased', 'compDesigns', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) => {
		const modesDependencies = designModes.getDependencies(component, refs)

		return Object.keys(modesDependencies.modes).length > 1
			? modesDependencies
			: {
				compDesign: compInfoNodes.compDesign.getDependencies(component, refs as NodeRefs<any>),
				isMobileView: envRefs.isMobileView,
			}
	},
	toViewItem: (component, dependencies) => {
		if ('modes' in dependencies) {
			return designModes.toViewItem(component, dependencies)
		}
		const { compDesign } = dependencies
		const items = compDesign
			? omitValuesAccordingToViewMode(
				isRefArray(compDesign as CompDesign | RefArrayViewItem<CompDesign>)
					? (((compDesign as RefArrayViewItem<CompDesign>).values as unknown) as Array<
								BaseDataItem | VariantRelationViewItem<BaseDataItem>
					>)
					: (([compDesign as CompDesign] as unknown) as Array<
								BaseDataItem | VariantRelationViewItem<BaseDataItem>
					>),
				dependencies.isMobileView
			)
			: []
		return { type: 'variants', css: items } as Design
	},
})

const handleBuilderStyleApi = (style: Style) => {
	const styleCssProperties = style.cssProperties || style.style?.properties || {}
	const variables = style.cssCustomProperties || {}

	const orderedProps: Array<[string, string]> = Object.entries(styleCssProperties).reduce(
		(acc: Array<[string, string]>, [key, value]) => {
			const fixedKey = _.kebabCase(key)
			const fixedValue = `${value}`

			const processedValue = fixedValue.includes('spx')
				? resolveSpxAttribute(fixedValue, style.spx, true)
				: fixedValue

			const propEntry: [string, string] = [fixedKey, processedValue]

			if (CSS_SHORTHAND_PROPERTIES.has(fixedKey)) {
				acc.unshift(propEntry)
			} else {
				acc.push(propEntry)
			}

			return acc
		},
		[]
	)

	const cssProperties = Object.fromEntries(orderedProps) // to ensure the order of the properties

	const cssCustomProperties = Object.entries(variables).reduce<Record<string, string>>((acc, [key, value]) => {
		const fixedValue = `${value}`

		if (fixedValue.includes('spx')) {
			acc[key] = resolveSpxAttribute(fixedValue, style.spx, true)
		} else {
			acc[key] = fixedValue
		}
		return acc
	}, {})

	return {
		...style,
		id: style.id || '',
		cssProperties,
		...(Object.keys(variables).length > 0 ? { cssCustomProperties } : {}),
	}
}
